import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Center,
  Divider,
  Flex,
  IconButton,
  Radio,
  RadioGroup,
  Skeleton,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { ContinueAndSkipButton } from '../../common/components/ContinueAndSkipButton';
import { trackTariffImportedFromWattbuy } from '../../utils/common/analytics';
import { trackDynatraceTariffImportedFromWattbuy } from '../../utils/native/dynatrace-events';
import { selectSite } from '../site/siteSlice';
import { MIN_TARIFF_PAGE_HEIGHT } from './tariff-constants';
import { getDefaultEffectiveDate } from './tariff-effective-date';
import { mapTariffApiToForm } from './tariff-helpers';
import { useConvertRetailRateToTariffMutation, useRetailRates, useTariffs } from './tariffApi';
import { useTariffFormContext } from './useTariffFormContext';

// can't set this manually yet
const PAGE_SIZE = 10;

type WattbuyRetailRatesSelectProps = {
  onSkip?: () => void;
  showSkip?: boolean;
  skipLabel?: React.ReactNode;
  skipTestId?: string;
};

export const WattbuyRetailRatesSelect = ({
  onSkip,
  showSkip = false,
  skipLabel,
  skipTestId,
}: WattbuyRetailRatesSelectProps) => {
  const { control, reset } = useTariffFormContext();
  const { tariffs } = useTariffs();
  const site = useSelector(selectSite);
  const toast = useToast({ duration: 3000, isClosable: true });
  const utility = useWatch({ name: 'tariff.utility', control });
  const { name, state } = utility;
  const [{ retailRateIndex, pageNumber }, setState] = useState<{ retailRateIndex?: number; pageNumber: number }>({
    retailRateIndex: undefined,
    pageNumber: 1,
  });
  const { retailRates, count, isLoading, isFetching } = useRetailRates(pageNumber);
  const { t } = useTranslation();

  const { canGoPreviousPage, canGoNextPage, showPagination } = useMemo(() => {
    let canGoPreviousPage = false;
    let canGoNextPage = false;
    if (count > PAGE_SIZE * pageNumber) canGoNextPage = true;
    if (pageNumber > 1) canGoPreviousPage = true;

    return { canGoPreviousPage, canGoNextPage, showPagination: canGoPreviousPage || canGoNextPage };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, pageNumber, retailRates]);

  const [convertRetailRate, { isLoading: isConvertingRetailRate }] = useConvertRetailRateToTariffMutation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleSubmit = async () => {
    try {
      if (retailRateIndex === undefined) throw Error('No retail rate selected');
      const retailRate = retailRates[retailRateIndex];
      const tariff = await convertRetailRate({
        utilityId: retailRate.utility_id,
        rateId: retailRate.id,
        state,
      }).unwrap();
      // Default the effective date to today (site timezone), only advancing to the next free day if
      // today already has a tariff. The plain reset (replacing defaultValues too) is load-bearing: it
      // drops default-only fields like zipCode from getValues(), which keeps useHandleSubmitForm from
      // running the utility→retailer conversion — the import already carries its retailer id.
      const tariffData = {
        tariff_effective_date: getDefaultEffectiveDate(tariffs, site.timezone),
        tariff,
      };
      reset(mapTariffApiToForm(tariffData, utility));

      trackTariffImportedFromWattbuy();
      trackDynatraceTariffImportedFromWattbuy();

      // show confirmation page
      navigate(`../${tariff.tariff_type.toLowerCase()}/confirm?backURL=${pathname}`);
    } catch (error) {
      console.error(error);
      if (!toast.isActive('fetch-retail-rate-error'))
        toast({
          title: t('Energy Rates.error fetching retail rate'),
          description: `${t('Common.please try again')} ${t('Energy Rates.if error persists fallback to manual')}`,
          status: 'error',
          id: 'fetch-retail-rate-error',
        });
    }
  };

  const listHeight = useMemo(
    () => `calc(${MIN_TARIFF_PAGE_HEIGHT} - ${showPagination ? 200 : 150}px)`,
    [showPagination]
  );

  // When the user selects a utility, scroll to the top of the rates list
  const topRef = useRef<HTMLDivElement>(null);
  const scrollToTop = () => {
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  useEffect(() => {
    scrollToTop();
  }, [utility]);

  return (
    <VStack align={'initial'} minHeight={MIN_TARIFF_PAGE_HEIGHT} gap={0} data-testid="retail-rates-select">
      <Text ref={topRef}>{t('Energy Rates.select the rate plan', { utilityName: name })}</Text>
      <Box
        maxHeight={listHeight}
        minHeight={listHeight}
        overflowY={'auto'}
        bg={'#ffffff'}
        _dark={{ bg: '#111111' }}
        borderRadius={4}
        data-testid="retail-rates-list"
      >
        <RadioGroup value={retailRateIndex?.toString()}>
          {isLoading ? (
            <VStack>
              {Array.from({ length: 5 }, (_, i) => i).map((index) => (
                <Skeleton w={'100%'} h={20} rounded={2} key={`skeleton-${index}`} />
              ))}
            </VStack>
          ) : (
            retailRates.map(({ id, rate_name: rateName, tariff_code: tariffCode, zone }, index) => {
              const formattedTariffCode = tariffCode ? `${tariffCode} - ` : '';
              const zoneId = zone ? `${zone} - ` : '';
              const retailRateName = `${formattedTariffCode}${zoneId}${rateName}`;
              return (
                <Box
                  key={id + retailRateName}
                  onClick={() => setState((prevState) => ({ ...prevState, retailRateIndex: index }))}
                  data-testid={`retail-rate-${index}`}
                >
                  <Box pl={4} py={4}>
                    <Flex mt={2} pr={4} w="100%" justify={'space-between'}>
                      <Text>{retailRateName}</Text>
                      <Radio
                        value={index.toString()}
                        h="fit-content"
                        my="auto"
                        ml={4}
                        data-testid={`${rateName}-radio-button`}
                      />
                    </Flex>
                  </Box>
                  <Divider />
                </Box>
              );
            })
          )}
        </RadioGroup>
      </Box>
      {showPagination && (
        <Center mt={2}>
          {canGoPreviousPage && (
            <IconButton
              isLoading={isFetching}
              icon={<ChevronLeftIcon w={6} h={6} />}
              rounded={'100%'}
              aria-label="go-to-previous-page"
              onClick={() => setState((prevState) => ({ ...prevState, pageNumber: pageNumber - 1 }))}
              data-testid="previous-page-button"
            />
          )}
          {canGoNextPage && (
            <IconButton
              isLoading={isFetching}
              icon={<ChevronRightIcon w={6} h={6} />}
              rounded={'100%'}
              aria-label="go-to-next-page"
              onClick={() => setState((prevState) => ({ ...prevState, pageNumber: pageNumber + 1 }))}
              data-testid="next-page-button"
            />
          )}
        </Center>
      )}

      <ContinueAndSkipButton
        pt={showPagination ? 2 : 4}
        continueLabel={t('Common.continue')}
        continueTestId="continue-button"
        continueButtonProps={{ w: '100%', py: 6, isDisabled: retailRateIndex === undefined }}
        isLoading={isConvertingRetailRate || isLoading}
        onContinue={handleSubmit}
        onSkip={onSkip}
        showSkip={showSkip}
        skipLabel={skipLabel}
        skipTestId={skipTestId}
      />
    </VStack>
  );
};
